import { Mutation } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import React, {
  PropsWithChildren,
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';
import {
  EbisDomain,
  EbisError,
  EbisRulesResponse,
  EbisWarning,
  EbisappWidgetRulesResponse,
} from '../data/types/common';
import {
  BulkClipboardStateChangeParam,
  EbisappRulesServiceData,
} from '../data/types/ebisappBulkUpdate';
import { InternationalAmateur } from '../data/types/internationalAmateur';
import { EmployeeMatchException } from '../data/types/person';
import { SchoolServiceMatchingException } from '../data/types/rule4';
import { InternationalAmateurMatchProvider } from './internationalAmateurMatchContext';

export type EbisMessagingContext<T extends EbisDomain = EbisDomain> = {
  showServerErrorModal: boolean;
  setShowServerErrorModal: (value: boolean) => void;

  showBulkUpdateErrorModal: boolean;
  setShowBulkUpdateErrorModal: (value: boolean) => void;

  showBulkServerErrorModal: boolean;
  setShowBulkServerErrorModal: (value: boolean) => void;

  showConfirmationModal: boolean;
  setShowConfirmationModal: (value: boolean) => void;

  ebisDomain: T;
  bulkUpdate: BulkClipboardStateChangeParam;
  ebisBulkDomains: EbisDomain[];

  setEbisDomain: (value: T) => void;
  setBulkUpdate: (value: BulkClipboardStateChangeParam) => void;
  setEbisBulkDomains: (value: EbisDomain[]) => void;
  mutation: Mutation<unknown, unknown, T>;
  setMutation: (value: Mutation<unknown, unknown, T>) => void;
  bulkMutation: Mutation<unknown, unknown, EbisDomain[]>;
  setBulkMutation: (value: Mutation<unknown, unknown, EbisDomain[]>) => void;

  errors: EbisError[];
  addError: (error: EbisError) => void;
  warnings: EbisWarning[];
  addWarning: (error: EbisWarning) => void;

  addRulesResponseMessages: (ebisRulesResponse: EbisRulesResponse) => void;
  bulkUpdateResponseMessages: EbisappRulesServiceData;
  setBulkUpdateResponseMessages: (
    bulkUpdateResponseMessages: EbisappRulesServiceData
  ) => void;
  widgetBulkRulesResponse: EbisappWidgetRulesResponse;
  setWidgetBulkRulesResponse: (
    ebisRulesResponse: EbisappWidgetRulesResponse
  ) => void;

  forbidden: boolean;
  setForbidden: React.Dispatch<React.SetStateAction<boolean>>;
  boReportError: boolean;
  setBoReportError: React.Dispatch<React.SetStateAction<boolean>>;
  concurrency: boolean;
  setConcurrency: React.Dispatch<React.SetStateAction<boolean>>;
  unhandledError: AxiosError;
  setUnhandledError: (value: AxiosError) => void;
  clearMessageContext: () => void;
  leftDraft: boolean;
  setLeftDraft: React.Dispatch<React.SetStateAction<boolean>>;
  loadedDraft: boolean;
  setLoadedDraft: React.Dispatch<React.SetStateAction<boolean>>;
  setOnContinue: (onContinue: () => void) => void;
  onContinue: () => void;
  onBack: () => void;
  setOnBack: (value: () => void) => void;

  onConfirm: () => void;
  setOnConfirm: (value: () => void) => void;
  confirmationMainText: string;
  setConfirmationMainText: (value: string) => void;
  confirmationSubText: string;
  setConfirmationSubText: (value: string) => void;
  /**
   * @deprecated This function is deprecated. Please use an alternative approach.
   * TODO: Get rid of this.
   */
  openConfirmationModal: (
    onConfirm: () => void,
    mainText?: string,
    subText?: string
  ) => void;
  intlMatchingError: InternationalAmateur;
  setIntlMatchingError: (value: InternationalAmateur) => void;
  schoolMatchingError: SchoolServiceMatchingException;
  setSchoolMatchingError: (value: SchoolServiceMatchingException) => void;
  employeeMatchingError: EmployeeMatchException;
  setEmployeeMatchingError: (value: EmployeeMatchException) => void;
  saveAndRunRuleChecks: boolean;
  setSaveAndRunRuleChecks: (value: boolean) => void;
  errorBoundaryResetKey: number;
  resetErrorBoundary: () => void;
};

const EbisMessagingContext =
  createContext<EbisMessagingContext<EbisDomain>>(undefined);

const EbisMessagingProvider: React.FC<PropsWithChildren> = ({ children }) => {
  const [saveAndRunRuleChecks, setSaveAndRunRuleChecks] = useState(false);
  const [boReportError, setBoReportError] = useState(false);
  const [showServerErrorModal, setShowServerErrorModal] =
    useState<boolean>(false);
  const [showBulkUpdateErrorModal, setShowBulkUpdateErrorModal] =
    useState<boolean>(false);
  const [showBulkServerErrorModal, setShowBulkServerErrorModal] =
    useState<boolean>(false);
  const [showConfirmationModal, setShowConfirmationModal] =
    useState<boolean>(false);
  const [ebisDomain, setEbisDomain] = useState<EbisDomain>(undefined);
  const [bulkUpdate, setBulkUpdate] =
    useState<BulkClipboardStateChangeParam>(undefined);
  const [ebisBulkDomains, setEbisBulkDomains] =
    useState<EbisDomain[]>(undefined);
  const [mutation, setMutation] =
    useState<Mutation<unknown, unknown, EbisDomain>>();
  const [bulkMutation, setBulkMutation] =
    useState<Mutation<unknown, unknown, EbisDomain[]>>();
  const [widgetBulkRulesResponse, setWidgetBulkRulesResponse] =
    useState<EbisappWidgetRulesResponse>(undefined);
  const [forbidden, setForbidden] = useState(false);
  const [unhandledError, setUnhandledError] = useState<AxiosError>();
  const [concurrency, setConcurrency] = useState(false);
  const [onContinue, setOnContinueInternal] = useState<() => void>(undefined);
  const [intlMatchingError, setIntlMatchingError] =
    useState<InternationalAmateur>(undefined);
  const [schoolMatchingError, setSchoolMatchingError] =
    useState<SchoolServiceMatchingException>(undefined);
  const [employeeMatchingError, setEmployeeMatchingError] =
    useState<EmployeeMatchException>(undefined);
  const [onBack, setOnBackInternal] = useState<() => void>(undefined);
  const [errors, setErrors] = useState<EbisError[]>([]);
  const [warnings, setWarnings] = useState<EbisWarning[]>([]);
  const [errorBoundaryResetKey, setErrorBoundaryResetKey] = useState(0);

  const [leftDraft, setLeftDraft] = useState(false);
  const [loadedDraft, setLoadedDraft] = useState(false);
  const [bulkUpdateResponseMessages, setBulkUpdateResponseMessages] =
    useState<EbisappRulesServiceData>();

  const [confirmationMainText, setConfirmationMainText] = useState<
    string | undefined
  >(undefined);
  const [confirmationSubText, setConfirmationSubText] = useState<
    string | undefined
  >(undefined);
  const [onConfirm, setOnConfirm] = useState<() => void>(undefined);

  const openConfirmationModal = useCallback(
    (onConfirm: () => void, mainText?: string, subText?: string) => {
      setConfirmationMainText(mainText);
      setConfirmationSubText(subText);
      setOnConfirm(() => onConfirm);
      setShowConfirmationModal(true);
    },
    []
  );

  const addRulesResponseMessages = useCallback(
    ({ errors, warnings }: EbisRulesResponse) => {
      setErrors(errors);
      setWarnings(warnings);
    },
    []
  );

  const addError = useCallback((error: EbisError) => {
    setErrors((prev) => [...prev, error]);
  }, []);

  const addWarning = useCallback((warning: EbisWarning) => {
    setWarnings((prev) => [...prev, warning]);
  }, []);

  const setOnContinue = useCallback(
    (onContinue: () => void) => {
      setOnContinueInternal(() => {
        return onContinue;
      });
    },
    [setOnContinueInternal]
  );

  const setOnBack = useCallback(
    (onBack: () => void) => {
      setOnBackInternal(() => {
        return onBack;
      });
    },
    [setOnBackInternal]
  );

  const resetErrorBoundary = useCallback(() => {
    setErrorBoundaryResetKey((prev) => prev + 1);
  }, []);

  const clearMessageContext = useCallback(() => {
    setErrors([]);
    setWarnings([]);
    setMutation(null);
    setEbisDomain(null);
    setBulkUpdate(null);
    setForbidden(false);
    setConcurrency(false);
    setShowServerErrorModal(false);
    setShowBulkUpdateErrorModal(false);
    setShowBulkServerErrorModal(false);
    setShowConfirmationModal(false);
    setBulkMutation(null);
    setEbisBulkDomains(null);
    setWidgetBulkRulesResponse(undefined);
    setBulkUpdateResponseMessages(undefined);
    setOnContinue(undefined);
    setBoReportError(false);
    setIntlMatchingError(undefined);
    setSchoolMatchingError(undefined);
    setEmployeeMatchingError(undefined);
    setOnBackInternal(undefined);
    setSaveAndRunRuleChecks(false);
  }, [setOnContinue, setOnBackInternal]);

  const value = useMemo(
    () => ({
      showServerErrorModal,
      setShowServerErrorModal,
      showBulkUpdateErrorModal,
      setShowBulkUpdateErrorModal,
      ebisDomain,
      setEbisDomain,
      bulkUpdate,
      setBulkUpdate,
      errors,
      addError,
      warnings,
      addWarning,
      addRulesResponseMessages,
      mutation,
      setMutation,
      unhandledError,
      setUnhandledError,
      forbidden,
      setForbidden,
      concurrency,
      setConcurrency,
      clearMessageContext,
      ebisBulkDomains,
      setEbisBulkDomains,
      bulkMutation,
      setBulkMutation,
      bulkUpdateResponseMessages,
      setBulkUpdateResponseMessages,
      widgetBulkRulesResponse,
      setWidgetBulkRulesResponse,
      showBulkServerErrorModal,
      setShowBulkServerErrorModal,
      leftDraft,
      setLeftDraft,
      onContinue,
      setOnContinue,
      loadedDraft,
      setLoadedDraft,
      showConfirmationModal,
      setShowConfirmationModal,
      setConfirmationMainText,
      confirmationMainText,
      setConfirmationSubText,
      confirmationSubText,
      onConfirm,
      setOnConfirm,
      openConfirmationModal,
      boReportError,
      setBoReportError,
      intlMatchingError,
      setIntlMatchingError,
      schoolMatchingError,
      setSchoolMatchingError,
      employeeMatchingError,
      setEmployeeMatchingError,
      onBack,
      setOnBack,
      saveAndRunRuleChecks,
      setSaveAndRunRuleChecks,
      errorBoundaryResetKey,
      resetErrorBoundary,
    }),
    [
      showServerErrorModal,
      setShowServerErrorModal,
      showBulkUpdateErrorModal,
      setShowBulkUpdateErrorModal,
      ebisDomain,
      setEbisDomain,
      bulkUpdate,
      setBulkUpdate,
      errors,
      addError,
      warnings,
      addWarning,
      addRulesResponseMessages,
      mutation,
      setMutation,
      unhandledError,
      setUnhandledError,
      forbidden,
      setForbidden,
      concurrency,
      setConcurrency,
      clearMessageContext,
      ebisBulkDomains,
      setEbisBulkDomains,
      bulkMutation,
      setBulkMutation,
      widgetBulkRulesResponse,
      setWidgetBulkRulesResponse,
      bulkUpdateResponseMessages,
      setBulkUpdateResponseMessages,
      showBulkServerErrorModal,
      setShowBulkServerErrorModal,
      leftDraft,
      setLeftDraft,
      onContinue,
      setOnContinue,
      loadedDraft,
      setLoadedDraft,
      showConfirmationModal,
      setShowConfirmationModal,
      setConfirmationMainText,
      confirmationMainText,
      setConfirmationSubText,
      confirmationSubText,
      onConfirm,
      setOnConfirm,
      openConfirmationModal,
      boReportError,
      setBoReportError,
      intlMatchingError,
      setIntlMatchingError,
      schoolMatchingError,
      setSchoolMatchingError,
      employeeMatchingError,
      setEmployeeMatchingError,
      onBack,
      setOnBack,
      saveAndRunRuleChecks,
      setSaveAndRunRuleChecks,
      errorBoundaryResetKey,
      resetErrorBoundary,
    ]
  );

  return (
    <EbisMessagingContext.Provider value={value}>
      <InternationalAmateurMatchProvider>
        {children}
      </InternationalAmateurMatchProvider>
    </EbisMessagingContext.Provider>
  );
};

const useEbisMessaging = <
  T extends EbisDomain = EbisDomain
>(): EbisMessagingContext<T> => {
  const context =
    useContext<EbisMessagingContext<EbisDomain>>(EbisMessagingContext);
  if (context === undefined) {
    throw new Error(
      `useEbisMessaging must be used within a EbisMessagingContext`
    );
  }
  return context as EbisMessagingContext<T>;
};

export { EbisMessagingProvider, useEbisMessaging };
